// TODO (07/21/2026): Remove this code when the dialog is no longer needed on 10/01/2026.
import { LitElement, html, css } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import '../katapult-elements/katapult-dialog.js';
import '../katapult-elements/katapult-button.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import { WorkflowManagementBetaAccessDialogService } from './WorkflowManagementBetaAccessDialogService.js';
/** @import { Ref } from 'lit/directives/ref.js' */
/** @import { KatapultDialog } from '../katapult-elements/katapult-dialog.js' */
/** @import { KatapultButton } from '../katapult-elements/katapult-button.js' */

const WORKFLOW_MANAGEMENT_GIF_URL = 'https://storage.googleapis.com/katapult-pro-shared-files/photos/workflow_management_announcement.gif';
const WORKFLOW_DOCUMENTATION_LINK = 'https://guide.katapultengineering.com/getting-started-with-workflow-management';
const WORKFLOW_VIDEO_LINK = 'https://www.loom.com/share/660981b1401345e09af8143862a33512';

/**
 * Presentational dialog inviting a company admin into the Workflow Management beta program.
 */
export class WorkflowManagementBetaAccessDialog extends LitElement {
  static styles = css`
    #title {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-x-small);
    }
    #body {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--sl-spacing-x-large);
      max-width: 600px;
      color: var(--sl-color-neutral-900);
    }
    #description {
      text-align: center;
      font-size: var(--sl-font-size-medium);
      font-weight: var(--sl-font-weight-semibold);
      color: var(--sl-color-neutral-800);
    }
    #description .feature-name {
      font-weight: var(--sl-font-weight-bold);
      color: var(--sl-color-primary);
    }
    #announcement-gif {
      align-self: center;
      border-radius: var(--sl-border-radius-large);
      box-shadow: var(--sl-shadow-medium);
    }
    #button-row {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      width: -webkit-fill-available;
      gap: var(--sl-spacing-small);
    }
    #dismiss-button {
      margin-right: auto;
    }
    sl-menu-item:hover::part(base),
    sl-menu-item:focus-visible::part(base) {
      background-color: var(--sl-color-neutral-200);
      color: var(--sl-color-neutral-900);
    }
  `;

  static properties = {
    earlyAccessAlreadyEnabled: { type: Boolean },
    userGroup: { type: String },
    userId: { type: String }
  };

  constructor() {
    super();
    /** @type {string} */
    this.userGroup = '';
    /** @type {string} */
    this.userId = '';
    /** @type {boolean} */
    this.earlyAccessAlreadyEnabled = false;
    /** @type {WorkflowManagementBetaAccessDialogService} */
    this.service = new WorkflowManagementBetaAccessDialogService(globalThis.FirebaseWorker, globalThis.firebase);

    /** @type {Ref<KatapultDialog>} */
    this.dialogRef = createRef();
  }

  render() {
    return html`
      <katapult-dialog ${ref(this.dialogRef)} dialog-color="var(--sl-color-primary)" @closed=${() => this.remove()}>
        <div id="title" slot="title">
          <katapult-icon icon="assignment"></katapult-icon>
          <span>New Experimental Feature - Workflow Management</span>
        </div>
        <div id="body">
          <div id="description">
            Organize and track your work with Workflows. Advance jobs through configurable statuses with powerful automations.
          </div>
          <img id="announcement-gif" src="${WORKFLOW_MANAGEMENT_GIF_URL}" alt="Workflow Management Katapult Pro" />
        </div>
        <div id="button-row" slot="buttons">
          <katapult-button id="dismiss-button" .callback=${() => this.dismissAndClose()}>Dismiss</katapult-button>
          <sl-dropdown hoist placement="top-start" @sl-select=${(event) => this.openSelectedLearnMoreResource(event)}>
            <katapult-button slot="trigger" outlined icon="info" .iconConfig=${{ outline: true }} color="var(--secondary-color)"
              >Learn More</katapult-button
            >
            <sl-menu>
              <sl-menu-item value="video">
                Watch Video
                <katapult-icon slot="prefix" color="var(--secondary-color)" icon="play_circle"></katapult-icon>
              </sl-menu-item>
              <sl-menu-item value="documentation">
                Read Documentation
                <katapult-icon slot="prefix" color="var(--secondary-color)" icon="menu_book"></katapult-icon>
              </sl-menu-item>
            </sl-menu>
          </sl-dropdown>
          <sl-tooltip content=${this.determineEnableButtonTooltipContent()} hoist>
            <div>
              <katapult-button
                color="var(--secondary-color)"
                ?disabled=${this.earlyAccessAlreadyEnabled}
                .callback=${(event) => this.enableEarlyAccessAndClose(event)}
                >Enable Workflow Management</katapult-button
              >
            </div>
          </sl-tooltip>
        </div>
      </katapult-dialog>
    `;
  }

  // Open the underlying dialog
  open() {
    this.dialogRef.value?.open();
  }

  // Close the underlying dialog
  close() {
    this.dialogRef.value?.close();
  }

  // Build the hover text for the enable button based on whether the company already has early access
  determineEnableButtonTooltipContent() {
    if (this.earlyAccessAlreadyEnabled) return 'Your company already has this feature enabled';
    return 'You can enable or disable this feature at any time via the Project Management page';
  }

  /**
   * Open the Workflow Management video or documentation in a new tab based on the selected menu item.
   * @param {CustomEvent} event The sl-select event carrying the chosen menu item.
   */
  openSelectedLearnMoreResource(event) {
    const selectedValue = event.detail?.item?.value;
    const resourceUrl = selectedValue === 'video' ? WORKFLOW_VIDEO_LINK : WORKFLOW_DOCUMENTATION_LINK;
    window.open(resourceUrl, '_blank');
  }

  // Record the dismissal against the active company and close the dialog
  dismissAndClose() {
    this.service.markWorkflowManagementBetaDialogDismissed(this.userGroup, this.userId);
    this.close();
  }

  /**
   * Enable the feature for the whole company then show success and close the dialog.
   * @param {Event} event The click event forwarded from the button callback.
   */
  async enableEarlyAccessAndClose(event) {
    const clickedButton = /** @type {KatapultButton} */ (event?.currentTarget);
    await this.service.enableWorkflowManagementEarlyAccessForCompany(this.userGroup);

    // Show the success state on the clicked button before closing so the success icon is visible
    clickedButton?.showSuccess();
    setTimeout(() => this.dismissAndClose(), 1800);
  }
}

window.customElements.define('workflow-management-beta-access-dialog', WorkflowManagementBetaAccessDialog);
